import React, { useState } from "react";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { addUser as addingUser } from "../../Actions/UserAction";
import { useDispatch, useSelector } from "react-redux";
import { InputField } from "../Common/InputField";
import { AppModeSelect } from "./AppModeSelect";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
const AddUser = () => {
  const dispatch = useDispatch();
  const today = new Date();
  const formRef = React.useRef(null);
  const isAddingUser = useSelector((state) => state.UserReducer?.isAddingUser);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "",
    userName: "",
    allowCompanies: 0,
    allowInvoices: 0,
    activeDate: "",
    expireDate: "",
    subscriptionInterval: "",
    appMode: [],
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  /* ------------------ HANDLE APP MODE CHANGE ------------------ */
  const handleAppModeChange = (modes) => {
    setFormData((prev) => ({ ...prev, appMode: modes }));
    setErrors((prev) => ({ ...prev, appMode: "" }));
  };

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "allowInvoices" || name === "allowCompanies") {
      const newValue = Number(value);
      if (newValue < 0) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "email",
      "password",
      "role",
      "allowCompanies",
      "allowInvoices",
      "activeDate",
      "expireDate",
      "subscriptionInterval",
      "appMode",
    ];

    requiredFields.forEach((field) => {
      const value = formData[field];
      if (field === "allowCompanies" || field === "allowInvoices") {
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          value === 0
        ) {
          newErrors[field] = "This field is required";
        } else if (Number(value) < 0) {
          newErrors[field] = "Value cannot be less than 0";
        }
      } else if (field === "appMode") {
        if (!value?.length) {
          newErrors[field] = "Select at least one app mode";
        }
      } else {
        if (!value || !value?.trim()) {
          newErrors[field] = "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    dispatch(
      addingUser(formData, setFormData, handleVisible, handleDangerVisible),
    );
  };
  return (
    <div className="w-full min-h-fit flex justify-center py-4">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        autoComplete="off"
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            fontSize: globalStyle.headingFontSizeSmall,
            color: globalStyle.curveDarkColor,
          }}
        >
          Add User
        </h2>

        {/* Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email */}
          <InputField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            error={errors.email}
            onChange={handleChange}
            required
          />
          {/* Password */}
          <div className="w-full">
            <label className="block mb-1 font-medium">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="********"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                  errors.password
                    ? "border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>
          {/* User Name */}
          <InputField
            label="User Name"
            name="userName"
            type="text"
            value={formData.userName}
            error={errors.userName}
            onChange={handleChange}
          />
          {/* Role */}
          <div>
            <label className="block mb-1 font-medium">
              Role <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.role ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select</option>
              <option value="admin">Admin</option>
              <option value="customer">Customer</option>
            </SearchableSelect>
            {errors.role && (
              <p className="text-red-500 text-sm mt-1">{errors.role}</p>
            )}
          </div>
          {/* Allow Companies */}
          <InputField
            label="Allow Companies"
            name="allowCompanies"
            type="number"
            value={formData.allowCompanies}
            error={errors.allowCompanies}
            onChange={handleChange}
            required={true}
          />
          {/* Allow Invoices */}
          <InputField
            label="Allow Invoices"
            name="allowInvoices"
            type="number"
            value={formData.allowInvoices}
            error={errors.allowInvoices}
            onChange={handleChange}
            required={true}
          />
          {/* Active Date */}
          <InputField
            label="Active Date"
            name="activeDate"
            type="date"
            min={today.toISOString().split("T")[0]}
            value={formData.activeDate}
            error={errors.activeDate}
            onChange={handleChange}
            required={true}
          />
          {/* Expires Date */}
          <InputField
            label="Expires Date"
            name="expireDate"
            type="date"
            value={formData.expireDate}
            error={errors.expireDate}
            onChange={handleChange}
            required={true}
          />
          {/* Subscription Interval */}
          <div>
            <label className="block mb-1 font-medium">
              Subscription Interval <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="subscriptionInterval"
              value={formData.subscriptionInterval}
              onChange={handleChange}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.subscriptionInterval
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            >
              <option value="">Select</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </SearchableSelect>
            {errors.subscriptionInterval && (
              <p className="text-red-500 text-sm mt-1">
                {errors.subscriptionInterval}
              </p>
            )}
          </div>
          {/* App Mode */}
          <AppModeSelect
            value={formData.appMode}
            error={errors.appMode}
            onChange={handleAppModeChange}
          />
        </div>

        {/* Submit button */}
        <div className="w-full flex justify-center">
          <button
            type="submit"
            error={errors.allowInvoices}
            onChange={handleChange}
            required={true}
          />
        </div>

        {/* Submit button */}
        <div className="w-full flex justify-center">
          <button
            type="submit"
            disabled={isAddingUser}
            className="w-full md:w-1/3 mt-4 py-2 rounded-md text-white font-bold disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              fontFamily: globalStyle.fontFamilyButton,
              background: globalStyle.buttonColor,
            }}
          >
            {isAddingUser ? "Please Wait...." : "ADD USER"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUser;
